import type { Permission } from '@kortex/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth, type SubscriptionSummary } from '../lib/auth';
import { initials, titleCase } from '../lib/format';
import { useToast } from './ui';
import { ActivatePlanPrompt } from './ActivatePlan';
import { PendingAcceptances } from './privacy/PendingAcceptances';
import { AccountModal } from './privacy/AccountModal';
import { AccountExitPanel } from './account/AccountExit';
import { ChangePasswordModal } from '../pages/Password';
import { TwoFactorModal } from '../pages/TwoFactor';
import { OfflineSetupModal } from '../pages/OfflineSetup';
import { DevicePairingModal } from '../pages/DevicePairingModal';
import { RegisterTerminalModal, useDeviceRegistration } from '../pages/DeviceSetup';
import { persistedDeviceId } from '../lib/deviceId';
import { isAndroidKiosk } from '../lib/androidBridge';
import { wipeThisDevice } from '../lib/localWipe';
import { browserOfflineModeEnabled, unsyncedLocalWork } from '../lib/posData';
import { isOpfsSqliteSupported } from '@kortex/pos-local/browser';
import { Assistant, HELP_SLOT_ID } from './Assistant';
import { NotificationBell } from './Notifications';
import { useTour } from './Tour';
import { useStackedTableLabels } from '../lib/useStackedTableLabels';

interface NavEntry {
  to: string;
  label: string;
  icon: string;
  permission: Permission;
  group: string;
}

const NAV: NavEntry[] = [
  { group: 'Operations', to: '/', label: 'Dashboard', icon: '◈', permission: 'dashboard:view' },
  { group: 'Operations', to: '/pos', label: 'POS Billing', icon: '▤', permission: 'pos:operate' },
  {
    group: 'Operations',
    to: '/online-orders',
    label: 'Online Orders',
    icon: '⇲',
    permission: 'pos:operate',
  },
  {
    group: 'Operations',
    to: '/kitchen',
    label: 'Kitchen Display',
    icon: '♨',
    permission: 'kds:view',
  },
  {
    group: 'Operations',
    to: '/front-desk',
    label: 'Front Desk',
    icon: '⌂',
    permission: 'rooms:view',
  },
  {
    group: 'Operations',
    to: '/reservations',
    label: 'Reservations',
    icon: '◷',
    permission: 'reservation:view',
  },
  {
    group: 'Operations',
    to: '/housekeeping',
    label: 'Housekeeping',
    icon: '✦',
    permission: 'housekeeping:view',
  },
  {
    group: 'Operations',
    to: '/invoices',
    label: 'Invoices',
    icon: '❑',
    permission: 'invoice:view',
  },
  {
    group: 'Operations',
    to: '/expenses',
    label: 'Expenses',
    icon: '¤',
    permission: 'expense:view',
  },
  {
    group: 'Kitchen & Stock',
    to: '/menu',
    label: 'Menu & Pricing',
    icon: '☰',
    permission: 'menu:view',
  },
  {
    group: 'Kitchen & Stock',
    to: '/offers',
    label: 'Offers',
    icon: '٪',
    permission: 'menu:view',
  },
  {
    group: 'Kitchen & Stock',
    to: '/inventory',
    label: 'Inventory',
    icon: '⛁',
    permission: 'inventory:view',
  },
  {
    group: 'Kitchen & Stock',
    to: '/procurement',
    label: 'Procurement',
    icon: '⇄',
    permission: 'procurement:view',
  },
  { group: 'People', to: '/people', label: 'Staff & Payroll', icon: '☺', permission: 'hr:view' },
  { group: 'Insight', to: '/day-book', label: 'Day Book', icon: '▦', permission: 'daybook:view' },
  {
    group: 'Insight',
    to: '/daily-report',
    label: 'Daily Reports',
    icon: '▥',
    permission: 'daybook:view',
  },
  { group: 'Insight', to: '/reports', label: 'Reports', icon: '◱', permission: 'reports:view' },
  { group: 'Insight', to: '/pnl', label: 'Profit & Loss', icon: '◲', permission: 'pnl:view' },
  { group: 'Insight', to: '/gst', label: 'GST Filing', icon: '₹', permission: 'gst:export' },
  {
    group: 'Administration',
    to: '/billing',
    label: 'Billing',
    icon: '₪',
    permission: 'settings:manage',
  },
  {
    group: 'Administration',
    to: '/payments',
    label: 'Payments',
    icon: '₹',
    permission: 'settings:manage',
  },
  {
    group: 'Administration',
    to: '/users',
    label: 'Users & Roles',
    icon: '⚿',
    permission: 'users:view',
  },
  {
    group: 'Administration',
    to: '/settings',
    label: 'Settings',
    icon: '⚙',
    /* Reading the property is something the floor screens do; opening the
       Administration screen is the owner's business. */
    permission: 'settings:manage',
  },
  {
    group: 'Administration',
    to: '/audit',
    label: 'Audit Log',
    icon: '≡',
    permission: 'audit:view',
  },
  {
    group: 'Administration',
    to: '/privacy-compliance',
    label: 'Privacy & Compliance',
    icon: '⚖',
    permission: 'privacy:view',
  },
  {
    group: 'Administration',
    to: '/device-management',
    label: 'Device Management',
    icon: '▣',
    /* Phase 7 (docs/android/PHASE-7-ARCHITECTURE.md): the fleet-wide
       registration/configuration/health/update surface, one level up
       from the single-device troubleshooting page below. */
    permission: 'property:manage',
  },
  {
    group: 'Administration',
    to: '/device-diagnostics',
    label: 'Device Diagnostics',
    icon: '⚕',
    /* Same gate as the POS terminals tab (pages/Admin.tsx) - hardware and
       sync troubleshooting is an owner concern, not a floor-staff one. */
    permission: 'property:manage',
  },
];

const PLATFORM_NAV = [
  { to: '/platform', label: 'Platform', icon: '◉' },
  { to: '/platform/hotels', label: 'Hotels', icon: '⌂' },
  { to: '/platform/plans', label: 'Plans & Pricing', icon: '₹' },
  { to: '/platform/landing', label: 'Landing Page', icon: '❝' },
  { to: '/platform/billing', label: 'Billing Setup', icon: '₪' },
  { to: '/platform/coupons', label: 'Coupons', icon: '🏷' },
  { to: '/platform/reports', label: 'Platform Reports', icon: '◱' },
  { to: '/platform/mail', label: 'Outgoing Mail', icon: '✉' },
  { to: '/platform/captcha', label: 'Sign-in Challenge', icon: '🛡' },
  { to: '/platform/errors', label: 'Server Errors', icon: '⚠' },
  { to: '/platform/privacy', label: 'Privacy & Governance', icon: '⚖' },
];

/** Where "Talk to us" sends a hotelier about their subscription. */
const SUPPORT_URL = 'https://www.europiumsolutions.com/contact-us/';

/**
 * Europium Solutions links, shown on every screen including the ones you reach
 * before signing in.
 *
 * Each opens in a new tab so a cashier mid-ticket does not lose the till, and
 * each carries noopener/noreferrer for the same reason the "Talk to us" button
 * does: the new tab must not reach back through window.opener.
 *
 * In the Android app a "new tab" is the phone's own browser: the shell hands
 * every external http(s) link to it (android/.../web/KortexWebView.kt). The two
 * pages that are ours, `internal`, are part of the app itself and have no
 * address a browser could open, so there they are ordinary in-app links.
 */
const FOOTER_LINKS = [
  { label: 'About Us', href: 'https://www.europiumsolutions.com/about-us/' },
  { label: 'Our Portfolio', href: 'https://www.europiumsolutions.com/portfolio/' },
  { label: 'Contact Us', href: SUPPORT_URL },
  /* Ours, not europiumsolutions.com, so it opens the version the app records
     acceptance against. */
  { label: 'Terms & Conditions', href: '/terms', internal: true },
  { label: 'Privacy Policy', href: '/privacy', internal: true },
] as const;

export function SiteFooter() {
  const kiosk = isAndroidKiosk();
  return (
    <footer className="site-footer">
      <nav>
        {FOOTER_LINKS.map((link) =>
          kiosk && 'internal' in link ? (
            <Link key={link.href} to={link.href}>
              {link.label}
            </Link>
          ) : (
            <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">
              {link.label}
            </a>
          ),
        )}
      </nav>
      <div className="footer-right">
        <span>© {new Date().getFullYear()} Europium Solutions</span>
        {/* Filled by the assistant where one is mounted; empty everywhere else. */}
        <span id={HELP_SLOT_ID} className="footer-help" />
      </div>
    </footer>
  );
}

export function Layout() {
  // Column labels for tables that turn into cards on a phone (styles.css, tbl-stack).
  useStackedTableLabels();
  const [changingPassword, setChangingPassword] = useState(false);
  const [account, setAccount] = useState(false);
  const [twoFactor, setTwoFactor] = useState(false);
  const [offlineSetup, setOfflineSetup] = useState(false);
  const [devicePairing, setDevicePairing] = useState(false);
  const [registeringTerminal, setRegisteringTerminal] = useState(false);
  // True whenever the OPFS/WASM SQLite driver is actually usable
  // (docs/offline/PHASE-8-ARCHITECTURE.md SS1) - including the Android
  // kiosk now, which sits on that same driver (see posData/index.ts) and
  // benefits from offline PIN unlock just as much as a browser tab does,
  // since it's what recovers a cashier from an accidental sign-out while
  // offline. Re-checked on every render rather than cached, since it can
  // never change without a page reload anyway.
  const offlineModeAvailable = isOpfsSqliteSupported();
  const { session, signOut, switchProperty, can, isPlatformAdmin, subscription, reloadSession } = useAuth();
  // Only ever asked on the Android app, by somebody who could act on the answer.
  const kioskDeviceId = isAndroidKiosk() && can('property:manage') ? persistedDeviceId() : undefined;
  const terminalRegistration = useDeviceRegistration(kioskDeviceId);

  /**
   * For a shared or personal browser that was set up for offline billing:
   * erase the local database and offline sign-in from it, then sign out.
   * Warns first if anything has not reached the server - erasing it loses it.
   */
  const removeOfflineData = async () => {
    setUserMenu(false);
    const unsynced = await unsyncedLocalWork().catch(() => 0);
    const warning =
      unsynced > 0
        ? `${unsynced} bill(s) on this browser have not reached the server yet and will be lost. Erase anyway?`
        : 'Erase the offline bills, menu and offline sign-in held in this browser? You can set offline access up again later.';
    if (!window.confirm(warning)) return;
    await wipeThisDevice();
    await signOut();
  };
  const { fail } = useToast();
  const location = useLocation();
  const [propertyMenu, setPropertyMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  // Following a link on a phone should reveal the page, not leave the drawer
  // sitting over it.
  useEffect(() => setNavOpen(false), [location.pathname]);
  const [theme, setTheme] = useState(() => localStorage.getItem('kortex-theme') ?? 'light');
  // Desktop/tablet/kiosk sidebar hide-away, distinct from the mobile drawer
  // (navOpen) above. A kiosk tablet has no stored preference on first boot,
  // so it starts collapsed - the POS screen gets the full width and staff
  // can bring the menu back with the topbar toggle whenever they need it.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem('kortex-sidebar-collapsed');
    return stored === null ? isAndroidKiosk() : stored === 'true';
  });
  const propertyRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('kortex-theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem('kortex-sidebar-collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    setNavOpen(false);
    setPropertyMenu(false);
    setUserMenu(false);
  }, [location.pathname]);

  // Close whichever popover is open when the click lands outside it.
  useEffect(() => {
    if (!propertyMenu && !userMenu) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (propertyMenu && !propertyRef.current?.contains(target)) setPropertyMenu(false);
      if (userMenu && !userRef.current?.contains(target)) setUserMenu(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [propertyMenu, userMenu]);

  if (!session) return null;

  const visible = NAV.filter((entry) => can(entry.permission));
  const groups = [...new Set(visible.map((entry) => entry.group))];
  const current = visible.find((entry) => entry.to === location.pathname);

  const onSwitch = async (propertyId: string) => {
    try {
      await switchProperty(propertyId);
    } catch (error) {
      fail(error);
    }
  };

  return (
    <div className={`app ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className={`sidebar no-print ${navOpen ? 'open' : ''}`}>
        <div className="brand">
          <div className="brand-mark">S</div>
          <div>
            <div className="brand-name">SARTHI</div>
            <div className="brand-sub">{session.property?.code ?? 'HMS Platform'}</div>
          </div>
        </div>

        {!session.property ? (
          <div className="switcher" style={{ cursor: 'default' }}>
            <div>
              <div className="sw-name">SARTHI Platform</div>
              <div className="sw-role">Superadmin</div>
            </div>
          </div>
        ) : session.properties.length > 1 ? (
          <div style={{ position: 'relative' }} ref={propertyRef}>
            <button className="switcher" onClick={() => setPropertyMenu((open) => !open)}>
              <div>
                <div className="sw-name">{session.property.name}</div>
                <div className="sw-role">{titleCase(session.role ?? '')}</div>
              </div>
              <span className="sw-caret">⌄</span>
            </button>
            {propertyMenu ? (
              <div className="menu-pop" style={{ left: 10, right: 10, top: 'calc(100% + 2px)' }}>
                <div className="head">Switch property</div>
                {session.properties.map((property) => (
                  <button key={property.id} onClick={() => void onSwitch(property.id)}>
                    <span>{property.name}</span>
                    <span className="xs muted" style={{ marginLeft: 'auto' }}>
                      {titleCase(property.role)}
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="switcher" style={{ cursor: 'default' }}>
            <div>
              <div className="sw-name">{session.property.name}</div>
              <div className="sw-role">{titleCase(session.role ?? '')}</div>
            </div>
          </div>
        )}

        <nav className="nav">
          {isPlatformAdmin ? (
            <div>
              <div className="nav-group">SARTHI Platform</div>
              {PLATFORM_NAV.map((entry) => (
                <NavLink
                  key={entry.to}
                  to={entry.to}
                  end={entry.to === '/platform'}
                  className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
                >
                  <span className="ico">{entry.icon}</span>
                  <span>{entry.label}</span>
                </NavLink>
              ))}
            </div>
          ) : null}
          {groups.map((group) => (
            <div key={group}>
              <div className="nav-group">{group}</div>
              {visible
                .filter((entry) => entry.group === group)
                .map((entry) => (
                  <NavLink
                    key={entry.to}
                    to={entry.to}
                    end={entry.to === '/'}
                    /* Every screen is a tour target without naming any of
                       them here - see lib/tours.ts. */
                    data-tour={`nav:${entry.to}`}
                    className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  >
                    <span className="ico">{entry.icon}</span>
                    <span>{entry.label}</span>
                  </NavLink>
                ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="row between">
            <span>{session.property?.gstin ?? 'Platform console'}</span>
            <button
              className="btn ghost sm"
              style={{ color: '#9fc4c0' }}
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              title="Toggle theme"
            >
              ◐
            </button>
          </div>
        </div>
      </aside>

      {navOpen ? (
        <button
          className="nav-scrim no-print"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <div className="main">
        <header className={`topbar no-print ${isAndroidKiosk() ? 'topbar-kiosk' : ''}`}>
          <button
            className="btn ghost icon sm nav-toggle"
            onClick={() => setNavOpen((open) => !open)}
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={navOpen}
          >
            ☰
          </button>
          <button
            className="btn ghost icon sm sidebar-toggle"
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
            aria-label={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
          >
            {sidebarCollapsed ? '»' : '«'}
          </button>
          <div>
            <div className="title">{current?.label ?? 'SARTHI'}</div>
            <div className="crumb">{session.property?.name ?? 'SARTHI Platform'}</div>
          </div>
          <div className="grow" />
          <TourButton />
          <NotificationBell />
          <div style={{ position: 'relative' }} ref={userRef}>
            <button className="user-chip" onClick={() => setUserMenu((open) => !open)}>
              <span className="avatar">{initials(session.user.name)}</span>
              <span className="small b">{session.user.name}</span>
              <span className="muted">⌄</span>
            </button>
            {userMenu ? (
              <div className="menu-pop">
                <div className="head">{session.user.email}</div>
                <div className="sep" />
                <button
                  onClick={() => {
                    setUserMenu(false);
                    setAccount(true);
                  }}
                >
                  My account
                </button>
                <button
                  onClick={() => {
                    setUserMenu(false);
                    setChangingPassword(true);
                  }}
                >
                  Change password
                </button>
                <button
                  onClick={() => {
                    setUserMenu(false);
                    setTwoFactor(true);
                  }}
                >
                  Two-step verification
                </button>
                {offlineModeAvailable ? (
                  <button
                    onClick={() => {
                      setUserMenu(false);
                      setOfflineSetup(true);
                    }}
                  >
                    Offline access
                  </button>
                ) : null}
                {isAndroidKiosk() && can('property:manage') ? (
                  <button
                    onClick={() => {
                      setUserMenu(false);
                      setDevicePairing(true);
                    }}
                  >
                    Pair this device
                  </button>
                ) : null}
                {/* Registering is the owner's choice: until they do, this device runs the app
                    online like the website, and only a registered one works offline. */}
                {kioskDeviceId && terminalRegistration.data && !terminalRegistration.data.registered ? (
                  <button
                    onClick={() => {
                      setUserMenu(false);
                      setRegisteringTerminal(true);
                    }}
                  >
                    Register this device as a terminal
                  </button>
                ) : null}
                {!isAndroidKiosk() && browserOfflineModeEnabled() ? (
                  <button onClick={() => void removeOfflineData()}>Remove offline data from this browser</button>
                ) : null}
                <button onClick={() => void signOut()}>Sign out</button>
              </div>
            ) : null}
          </div>
        </header>

        <main className="view">
          {subscription && subscription.status !== 'ACTIVE' && subscription.isEntitled ? (
            <div className="sub-banner">
              <span className="chip">{subscription.planName}</span>
              <span>
                {subscription.status === 'TRIALING'
                  ? subscription.daysRemaining === 0
                    ? 'Your free trial ends today.'
                    : `Your free trial has ${subscription.daysRemaining} ${subscription.daysRemaining === 1 ? 'day' : 'days'} left.`
                  : subscription.graceEndsAt
                    ? /* Grace is only worth giving if they know they have it -
                         and know when it runs out. */
                      `Your plan has ended. Access continues until ${new Date(
                        subscription.graceEndsAt,
                      ).toLocaleDateString('en-IN', {
                        day: 'numeric',
                        month: 'long',
                      })} — renew before then to keep working.`
                    : `Your subscription is ${subscription.statusLabel.toLowerCase()}.`}
              </span>
              {can('settings:manage') ? (
                subscription.graceEndsAt ? (
                  <Link className="btn sm primary" to="/billing">
                    Renew now
                  </Link>
                ) : (
                  /* noopener stops the new tab reaching back through window.opener;
                     noreferrer withholds the URL it was opened from. */
                  <a className="btn sm" href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                    Talk to us
                  </a>
                )
              ) : null}
            </div>
          ) : null}

          {subscription?.accessState === 'READ_ONLY' ? (
            <div className="sub-banner">
              <span className="chip warn">Read only</span>
              <span>
                Your subscription has ended. You can still read and download your data
                {subscription.accessUntil ? ` until ${new Date(subscription.accessUntil).toLocaleDateString()}` : ''}, but
                not change anything. Choose a plan on Billing to carry on.
              </span>
            </div>
          ) : null}
          {subscription?.accessState === 'EXPORT_ONLY' ? (
            // Closing: nothing but the way out.
            session?.role === 'OWNER' ? (
              <AccountExitPanel />
            ) : (
              <div className="card">
                <div className="card-body">This account is closing. Ask the owner for anything you need from it.</div>
              </div>
            )
          ) : subscription &&
            !subscription.isEntitled &&
            subscription.accessState !== 'READ_ONLY' &&
            !PAYABLE_WHILE_BLOCKED.has(location.pathname) ? (
            <SubscriptionEnded subscription={subscription} isOwner={session?.role === 'OWNER'} />
          ) : (
            <Outlet />
          )}
        </main>

        <SiteFooter />
      </div>

      {/* Nothing paid for yet: say so once, on the screen they land on. */}
      <ActivatePlanPrompt />
      <PendingAcceptances />
      {account ? <AccountModal onClose={() => setAccount(false)} /> : null}

      <Assistant />

      {changingPassword ? <ChangePasswordModal onClose={() => setChangingPassword(false)} /> : null}
      {session?.user.mustChangePassword ? (
        <ChangePasswordModal forced onClose={() => void reloadSession()} />
      ) : null}
      {twoFactor ? <TwoFactorModal onClose={() => setTwoFactor(false)} /> : null}
      {offlineSetup ? <OfflineSetupModal onClose={() => setOfflineSetup(false)} /> : null}
      {devicePairing ? <DevicePairingModal onClose={() => setDevicePairing(false)} /> : null}
      {registeringTerminal && kioskDeviceId ? (
        <RegisterTerminalModal
          deviceId={kioskDeviceId}
          onClose={() => setRegisteringTerminal(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * What a hotel sees once its subscription has ended.
 *
 * Not a banner over a screen that cannot load: every API call answers 402 at
 * this point, so the screens behind would be a wall of errors. The owner is
 * the only person who can do anything about it, so they get the two things
 * that help - renew, or talk to us - and everybody else gets told plainly
 * whose job it is, rather than being left to wonder what they broke.
 */
/**
 * Screens that stay open to a hotel whose subscription is not paid up.
 *
 * Without this the gate swallowed the way out of it. Every route rendered the
 * "pay for your plan" screen, including `/billing` - so the button on that
 * screen changed the URL and then drew the very same thing again, and a hotel
 * that wanted to pay had no route to doing it. The API has always allowed
 * this: billingRouter is mounted before requireEntitlement, for exactly this
 * reason. It was the layout that had not been told.
 */
const PAYABLE_WHILE_BLOCKED = new Set(['/billing']);

/**
 * Restarts the tour for whoever is signed in.
 *
 * The tour offers itself once and then stays out of the way, which left it
 * reachable only through Help - two taps and a menu, in a place people look
 * when they already know they are stuck. A cashier three days in who wants
 * the walk-through again has no reason to think of it as "help".
 *
 * So it sits in the topbar on every screen, for every role: each of the eight
 * has a tour, and the button simply disappears for anyone whose permissions
 * trim theirs down to nothing - a platform admin, most often, who has no
 * property role at all.
 *
 * Icon-only, because the topbar is already tight on a till screen and this is
 * the least important thing on it.
 */
function TourButton() {
  const { available, running, start } = useTour();
  if (!available) return null;

  return (
    <button
      className="btn ghost icon sm"
      onClick={start}
      disabled={running}
      aria-label={running ? 'Tour in progress' : `Take the tour: ${available.title}`}
      title={running ? 'Tour in progress' : `Take the tour: ${available.title}`}
    >
      🧭
    </button>
  );
}

function SubscriptionEnded({
  subscription,
  isOwner,
}: {
  subscription: SubscriptionSummary;
  isOwner: boolean;
}) {
  /*
   * A hotel that has never paid is not a hotel whose plan expired.
   *
   * Someone who has just signed up on a plan with no trial lands here, and
   * telling them their brand-new subscription has "ended" reads like a fault.
   * What they need is the way to pay, which they can now do themselves.
   */
  const awaitingFirstPayment =
    subscription.status === 'PENDING_PAYMENT' || subscription.status === 'PAYMENT_FAILED';

  const heading = awaitingFirstPayment
    ? subscription.status === 'PAYMENT_FAILED'
      ? 'That payment did not go through'
      : 'One step left — pay for your plan'
    : isOwner
      ? 'Your plan has expired'
      : 'Your current plan has expired';

  return (
    <div className="ended-shell">
      <div className="card ended-card">
        <div className="card-body col gap-16">
          <div className="row gap-6 wrap">
            <span className="chip danger">{subscription.statusLabel}</span>
            <span className="chip">{subscription.planName}</span>
          </div>

          <h2>{heading}</h2>

          {isOwner ? (
            <>
              <p className="small">
                {awaitingFirstPayment ? (
                  <>
                    Your hotel is set up and {subscription.planName} is ready to start — it begins
                    the moment your first payment goes through. Nothing has been charged yet.
                  </>
                ) : (
                  <>
                    {subscription.planName} ended
                    {subscription.endsAt
                      ? ` on ${new Date(subscription.endsAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}`
                      : ''}
                    . Your data is safe and nothing has been deleted — the hotel is closed for
                    writing until the plan is renewed.
                  </>
                )}
              </p>
              <div className="row gap-6 wrap">
                {/* Billing is mounted before the entitlement gate, so this is
                    one of the few screens that still works from here. */}
                <Link className="btn primary" to="/billing">
                  {awaitingFirstPayment ? 'Pay and get started' : 'Renew my plan'}
                </Link>
                <a className="btn" href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
                  Talk to us
                </a>
              </div>
              <p className="xs muted">
                {awaitingFirstPayment
                  ? 'Everything you have entered is saved and waiting. Access opens as soon as the payment is confirmed.'
                  : 'Once it is renewed, sign out and back in — or just refresh — and everything is where you left it.'}
              </p>
            </>
          ) : (
            <>
              <p className="small">
                {awaitingFirstPayment
                  ? 'This hotel is waiting on its first payment. The owner can complete it from the billing page.'
                  : 'Your current plan has expired, reach out to owner for upgrade.'}
              </p>
              <p className="xs muted">
                Nothing has been lost. The hotel is closed for writing until the owner
                {awaitingFirstPayment ? ' completes it' : ' renews it'}.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="view-head">
      <div>
        <h1>{title}</h1>
        {subtitle ? <div className="sub">{subtitle}</div> : null}
      </div>
      {actions ? <div className="row gap-6">{actions}</div> : null}
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: T; label: string }>;
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="page-tabs">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={tab.id === active ? 'active' : ''}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
